import {
  DermloopClinicalActionTypeEnum,
  DermloopRoleStatus,
  DermloopUserMustResetPasswordEnum,
  DermloopUserRoleEnum,
} from '@dermloop/static-values';
import {
  IsBoolean,
  IsInt,
  IsDate,
  IsEmail,
  IsEnum,
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Min,
  ValidateIf,
} from 'class-validator';
import { BaseDto } from './base.dto';
import type { GenderDto } from './clinical-details';
import { DAHTUserDetailsDto } from './daht';
import type { OccupationDto } from './occupation.dtos';
import type { PushDeviceDto } from './push-device.dto';
import type { StudyParticipationDto } from './studies/study-participation.dto';
import type { UserAffiliationDto } from './user-affiliation.dtos';
import type { UserRoleDto } from './user-role.dtos';

/** This is only used in the MitID migration phase and will be removed afterwards **/
export class UserMitIDMigrationDto {
  @IsUUID()
  @IsNotEmpty()
  uuid: string;
}

export { DermloopUserMustResetPasswordEnum };

export interface UserDto extends BaseDto {
  auth0Sub?: string;

  kratosId?: string | null;

  sub?: string;

  oneLoginSub?: string;

  legacyIdentifier?: string;

  // Has the user in the current session been MFA authenticated?
  mfaActive?: boolean;

  firstName?: string;

  lastName?: string;

  npi?: string;

  birthDate?: Date;

  email?: string;

  emailVerified?: boolean;

  phoneNumberPrefix?: string;

  phoneNumber?: string;

  phoneNumberVerified?: boolean;

  profileImageIdentifier?: string;

  occupation?: OccupationDto;
  dermoscopyExperienceMonths?: number;
  studies?: StudyParticipationDto[];

  pushDevices?: PushDeviceDto[];
  conditionDiagnosticExperienceMonths?: number;

  /** @deprecated Use conditionDiagnosticExperienceMonths */
  skinConditionDiagnosticExperienceMonths?: number;

  gender?: GenderDto;

  userRoles?: UserRoleDto[];

  consentGiven?: boolean;

  consentGivenDate?: Date;

  mustReceiveTeleReferralMail?: boolean;

  dahtDetails?: DAHTUserDetailsDto;

  deleteRequestedDate?: Date;

  migratedToKratos?: boolean;
  mustResetPassword?: DermloopUserMustResetPasswordEnum;

  smartPhrasesEnabled?: boolean;

  userAffiliations?: UserAffiliationDto[];
  // @deprecated use userAffiliations instead. Can be removed after April 1st, 2026 (issue #7253).
  medicalUnitUserAffiliations?: UserAffiliationDto[];
}

/** Used internally in API */
export interface CreateUserDto {
  // Role information
  medicalUnitId?: string;
  roles?: DermloopUserRoleEnum[];

  // User information
  firstName?: string;
  lastName?: string;
  email?: string;
  emailVerified?: boolean;
  profileImageIdentifier?: string;
  phoneNumber?: string;
  phoneNumberPrefix?: string;
  phoneNumberVerified?: boolean;
  consentGiven?: boolean;
  mustResetPassword?: DermloopUserMustResetPasswordEnum;
  smartPhrasesEnabled?: boolean;

  // Subs
  sub: string;
  auth0Sub?: string;
  oneLoginSub?: string;
  kratosId?: string;
  migratedToKratos?: boolean;

  // Metadata used for unittests
  id?: string;
  created?: Date;
  updated?: Date;
  npi?: string;

  affiliation?: {
    invitationAccepted: boolean;
    invitationExpiry?: Date;
    invitationCode?: string;
    readonly?: boolean;
    expiry?: Date;
    occupationId?: string;
    specialisationId?: string;
    allowedClinicalActions?: DermloopClinicalActionTypeEnum[];
    email?: string;
    invitationNpi?: string;
  };
}

/**
 * Used for unit user list.
 * */
export interface UserSearchResultDto {
  id: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  phoneNumberPrefix?: string;
  npi?: string;
  email?: string;
  created?: string | Date;
  /** The login method, e.g. `dkid|`; the sub itself is never sent. */
  subPrefix?: string;
  /** Login ids stay server-side; `never` makes leaking one a compile error. */
  sub?: never;
  oneLoginSub?: never;
  userRoles?: UserRoleDto[];
  deletedUsers?: boolean;
  vacationUntil?: Date;
  upcomingVacation?: Date;
  /**
   * True when the user has never belonged to a medical unit, jurisdictional
   * group or federation. Decided by the API because it reads the affiliation
   * table directly — the affiliations sent here are filtered by the query, so
   * the same fact cannot be re-derived from them.
   */
  belongsNowhere?: boolean;
  userAffiliations?: UserAffiliationDto[];
  // @deprecated use userAffiliations instead. Can be removed after April 1st, 2026 (issue #7253).
  medicalUnitUserAffiliations?: UserAffiliationDto[];
}

/**
 * DTO for querying users.
 */
export class UserSearchQueryDto {
  @IsInt()
  @Min(0)
  skip: number;

  @IsInt()
  @Min(1)
  take: number;

  @IsOptional()
  @IsString()
  searchText?: string;

  @IsOptional()
  @IsEnum(DermloopUserRoleEnum, { each: true })
  userRoles?: DermloopUserRoleEnum[];

  @IsEnum(DermloopRoleStatus, { each: true })
  @IsOptional()
  userRoleStatus?: DermloopRoleStatus[];

  @IsUUID('all', { each: true })
  @IsOptional()
  medicalUnitIds?: string[];

  /**
   * Widens `medicalUnitIds` to also return users who belong to no medical unit
   * at all, instead of restricting the result to the listed units.
   */
  @IsOptional()
  @IsBoolean()
  includeUsersWithoutMedicalUnit?: boolean;

  /** Drops Learn accounts: those whose `sub` is still their `auth0Sub`. */
  @IsOptional()
  @IsBoolean()
  excludeLearnUsers?: boolean;

  @IsUUID('all', { each: true })
  @IsOptional()
  jurisdictionalGroupIds?: string[];
}

export class UserInvitationDto {
  @IsString()
  email: string;

  @IsOptional()
  @IsString()
  npi?: string;

  @IsEnum(DermloopUserRoleEnum)
  role: DermloopUserRoleEnum;

  @IsUUID('all')
  jurisdictionalGroupId: string;

  @IsOptional()
  @IsUUID('all')
  medicalUnitId?: string;
}

/**
 * DTO for adding a UserRole to a user.
 */
export class AddUserRoleDto {
  @IsNotEmpty()
  @IsUUID()
  userId: string;

  @IsEnum(DermloopUserRoleEnum)
  role: DermloopUserRoleEnum;

  @IsOptional()
  @IsUUID()
  medicalUnitId?: string;

  @IsOptional()
  @IsUUID()
  medicalUnitGroupId?: string;

  @IsOptional()
  @IsUUID()
  jurisdictionalGroupId?: string;

  @IsOptional()
  @IsUUID()
  federationId?: string;
}

export class UpdateUserDto {
  @IsOptional()
  @IsString()
  firstName?: string;

  @IsOptional()
  @IsString()
  lastName?: string;

  @IsOptional()
  @IsDate()
  birthDate?: Date;

  @IsOptional()
  @IsString()
  npi?: string;

  @IsOptional()
  @IsEmail()
  email?: string | null;

  @ValidateIf((dto) => !!dto.phoneNumber)
  @Matches('^\\+\\d+$')
  @IsString()
  phoneNumberPrefix?: string;

  @ValidateIf((dto) => !!dto.phoneNumberPrefix)
  @IsString()
  phoneNumber?: string;

  @IsOptional()
  @IsBoolean()
  phoneNumberVerified?: boolean;

  @IsOptional()
  @Min(0)
  dermoscopyExperienceMonths?: number;

  @IsOptional()
  @Min(0)
  conditionDiagnosticExperienceMonths?: number;

  /** @deprecated Use conditionDiagnosticExperienceMonths */
  @IsOptional()
  @Min(0)
  skinConditionDiagnosticExperienceMonths?: number;

  @IsOptional()
  @IsUUID()
  genderId?: string;

  @IsOptional()
  @IsBoolean()
  consentGiven?: boolean;

  @IsOptional()
  @IsBoolean()
  mustReceiveTeleReferralMail?: boolean;

  @IsOptional()
  @IsDate()
  deleteRequestedDate?: Date;

  @IsOptional()
  @IsBoolean()
  smartPhrasesEnabled?: boolean;
}

export type UserNames = {
  firstName: string;
  lastName: string;
};
